import { useCallback, useState, useMemo } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { usePropertyData } from './usePropertyData';
import { usePropertyImageUpload } from './usePropertyImageUpload';
import useAppStore from '../../../store/appStore';

/**
 * PropertyTabContainer
 * 
 * Single source of truth for Property tab state and Firebase integration.
 * Manages:
 * - Loading properties from Firestore (real-time)
 * - Creating, editing, deleting properties
 * - Uploading property images
 * - Property form responses (real-time)
 * - Syncing state between Desktop and Mobile views
 */
export function usePropertyTabContainer() {
    const { user } = useAuth();

    // Property data hook (CRUD + state management with real-time listener)
    const propertyData = usePropertyData();

    // Local UI state for drawer
    const [showPropertyDrawer, setShowPropertyDrawer] = useState(false);
    const [activeDrawerTab, setActiveDrawerTab] = useState('editor');

    // Form responses from Zustand store (real-time via realtimeSubscriptions)
    const allFormResponses = useAppStore((s) => s.ctFormResponses);

    // CRM leads from Zustand store (real-time via realtimeSubscriptions)
    const allLeads = useAppStore((s) => s.leads);

    // Count responses for the currently open property (drawer tab count)
    const responseCount = useMemo(() => {
        if (!propertyData.currentPropertyId || !Array.isArray(allFormResponses)) return 0;
        return allFormResponses.filter(r => r.propertyId === propertyData.currentPropertyId).length;
    }, [allFormResponses, propertyData.currentPropertyId]);

    // Build a map of propertyId -> CRM lead count for the property hub list
    // Counts leads whose interestedProperties array contains the property ID
    const leadCountByProperty = useMemo(() => {
        if (!Array.isArray(allLeads)) return {};
        const counts = {};
        allLeads.forEach(lead => {
            const props = lead.interestedProperties;
            if (Array.isArray(props)) {
                props.forEach(p => {
                    if (p && p.id) {
                        counts[p.id] = (counts[p.id] || 0) + 1;
                    }
                });
            }
        });
        return counts;
    }, [allLeads]);

    // Image upload hook (called when image is selected)
    const imageUpload = usePropertyImageUpload({
        userId: user?.uid,
        propertyId: propertyData.currentPropertyId,
        onUrlUpdate: (imageUrl) => {
            // Update local state immediately for instant UI feedback
            if (!propertyData.currentProperty.thumbnailUrl) {
                propertyData.updateImageUrl('thumbnailUrl', imageUrl);
            }
            // First image also becomes thumbnail automatically
            if (propertyData.currentProperty.imageUrls.length === 0) {
                propertyData.updateImageUrl('thumbnailUrl', imageUrl);
            }
        },
    });

    // ==================== CREATE ====================
    const handleAddProperty = useCallback(async () => {
        const propertyId = await propertyData.createNewProperty({});

        if (propertyId) {
            setShowPropertyDrawer(true);
            setActiveDrawerTab('editor');
        }
    }, [propertyData]);

    // ==================== READ ====================
    const handlePropertyClick = useCallback(async (propertyId) => {
        await propertyData.loadProperty(propertyId);
        setShowPropertyDrawer(true);
        setActiveDrawerTab('editor');
    }, [propertyData]);

    // ==================== UPDATE ====================

    // Page Editor Tab - Update individual field
    const handleUpdateField = useCallback((field, value) => {
        propertyData.updateField(field, value);
    }, [propertyData]);

    // Page Editor Tab - Update multiple fields at once
    const handleUpdateFields = useCallback(async (updates) => {
        await propertyData.updateFields(updates);
    }, [propertyData]);

    // Form & Logic Tab - Update capture form fields
    const handleAddFormField = useCallback((fieldName) => {
        if (!fieldName.trim()) return;

        const newField = {
            name: fieldName,
            type: 'text',
            required: false,
            locked: false,
        };

        const updatedFields = [
            ...propertyData.currentProperty.captureFormFields,
            newField,
        ];

        propertyData.updateField('captureFormFields', updatedFields);
    }, [propertyData]);

    const handleRemoveFormField = useCallback((fieldName) => {
        const fields = propertyData.currentProperty.captureFormFields || [];
        const field = fields.find(f => f.name === fieldName);

        // Cannot remove locked/system fields
        if (!field || field.locked) return false;

        const updatedFields = fields.filter(f => f.name !== fieldName);
        propertyData.updateField('captureFormFields', updatedFields);
        return true;
    }, [propertyData]);

    // Form & Logic Tab - Update tags
    // handleTagsChange is the primary interface for TagSelector (receives full array)
    const handleTagsChange = useCallback((newTags) => {
        propertyData.updateField('tags', newTags || []);
    }, [propertyData]);

    // Legacy handlers for backward compatibility
    const handleAddTag = useCallback((tagInput) => {
        if (!tagInput.trim()) return;
        const tag = tagInput.trim();
        const currentTags = propertyData.currentProperty.tags || [];
        // Prevent duplicates (case-insensitive)
        if (currentTags.some(t => t.toLowerCase() === tag.toLowerCase())) return;
        const newTags = [...currentTags, tag];
        propertyData.updateField('tags', newTags);
    }, [propertyData]);

    const handleRemoveTag = useCallback((index) => {
        const newTags = (propertyData.currentProperty.tags || []).filter((_, i) => i !== index);
        propertyData.updateField('tags', newTags);
    }, [propertyData]);

    // Toggle an automation flag (nested field) and persist
    // Special handling: when createLead is disabled, also clear automation tags
    const handleToggleAutomation = useCallback((automationKey, value) => {
        propertyData.updateNestedField('automations', automationKey, value);

        // Keep createLead and addToLeads in sync for backward compatibility
        if (automationKey === 'createLead') {
            propertyData.updateNestedField('automations', 'addToLeads', value);
            // If disabling createLead, clear automation tags
            if (!value) {
                propertyData.updateNestedField('automations', 'tags', []);
            }
        } else if (automationKey === 'addToLeads') {
            propertyData.updateNestedField('automations', 'createLead', value);
            if (!value) {
                propertyData.updateNestedField('automations', 'tags', []);
            }
        }
    }, [propertyData]);

    // Update automation tags (tags applied to leads created from this property's form)
    const handleAutomationTagsChange = useCallback((tags) => {
        propertyData.updateNestedField('automations', 'tags', tags || []);
    }, [propertyData]);

    // ==================== DELETE ====================
    const handleDeleteProperty = useCallback(async () => {
        if (!confirm('Are you sure you want to delete this property? This action cannot be undone.')) {
            return;
        }

        const success = await propertyData.deleteCurrentProperty();
        if (success) {
            setShowPropertyDrawer(false);
        }
    }, [propertyData]);

    // ==================== IMAGES ====================
    const handleUploadImage = useCallback(async (file) => {
        if (!propertyData.currentPropertyId) {
            console.error('No property selected for image upload');
            return null;
        }

        const url = await imageUpload.uploadImage(file);
        return url;
    }, [propertyData.currentPropertyId, imageUpload]);

    const handleSetThumbnail = useCallback(async (imageUrl) => {
        await imageUpload.setAsThumbnail(imageUrl);
    }, [imageUpload]);

    // ==================== CLOSE DRAWER ====================
    const handleCloseDrawer = useCallback(() => {
        setShowPropertyDrawer(false);
        propertyData.clearCurrent();
    }, [propertyData]);

    // ==================== SAVE ====================
    const handleSaveProperty = useCallback(async () => {
        return await propertyData.saveAllFields();
    }, [propertyData]);

    // ==================== COMPUTED VALUES ====================
    // Format properties for display in tab UI
    const displayProperties = useMemo(() => {
        return propertyData.properties.map(prop => ({
            id: prop.id,
            address: prop.address || 'Untitled',
            price: formatPrice(prop.price),
            leads: leadCountByProperty[prop.id] || 0,
            views: prop.stats?.totalViews || 0,
            sqft: prop.sqft,
            bedrooms: prop.bedrooms,
            bathrooms: prop.bathrooms,
            // Use the first uploaded image (imageUrls[0]) as the preview if available.
            image: (prop.imageUrls && prop.imageUrls[0]) || prop.thumbnailUrl || 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&w=150&q=80',
        }));
    }, [propertyData.properties, leadCountByProperty]);

    return {
        // State
        properties: displayProperties,
        currentProperty: propertyData.currentProperty,
        currentPropertyId: propertyData.currentPropertyId,
        loading: propertyData.loading,
        error: propertyData.error,
        uploading: imageUpload.uploading,
        uploadError: imageUpload.uploadError,
        showPropertyDrawer,
        activeDrawerTab,
        isDirty: propertyData.isDirty,
        saving: propertyData.saving,

        // Responses state (real-time, derived from Zustand store)
        responseCount,

        // Drawer controls
        setShowPropertyDrawer,
        setActiveDrawerTab,
        handleCloseDrawer,

        // List actions
        handleAddProperty,
        handlePropertyClick,

        // Edit actions
        handleUpdateField,
        handleUpdateFields,
        handleDeleteProperty,

        // Form field actions
        handleAddFormField,
        handleRemoveFormField,

        // Tag actions
        handleTagsChange,
        handleAddTag,
        handleRemoveTag,

        // Automation actions
        handleToggleAutomation,
        handleAutomationTagsChange,

        // Image actions
        handleUploadImage,
        handleSetThumbnail,

        // Save action
        handleSaveProperty,
    };
}

/**
 * Helper: Format price as currency string
 */
function formatPrice(price) {
    if (!price) return '$0';
    const num = typeof price === 'string' ? parseInt(price.replace(/\D/g, ''), 10) : price;
    return `$${num.toLocaleString()}`;
}
